import { useState, useEffect } from 'react'
import {
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  DialogContent,
  DialogTitle,
  Tabs,
  Tab,
  useTheme,
} from '@mui/material'
import JsonView from 'react18-json-view'
import 'react18-json-view/src/style.css'
import {
  getDataMapFromCommonResponse,
  getPriceStringWithCurrency,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getSPDataWithParams } from 'src/supportPortal/services/services'

function TabPanel({ children, value, index, ...other }) {
  const theme = useTheme()
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`payment-tabpanel-${index}`}
      aria-labelledby={`payment-tab-${index}`}
      {...other}
    >
      {value === index && <div style={{ paddingTop: theme.spacing(2) }}>{children}</div>}
    </div>
  )
}

function getReconciliationAdjustment(reportItem) {
  if (reportItem?.reconciliationAdjustment) {
    return reportItem.reconciliationAdjustment
  }
  if (!reportItem?.reconciliationApplied) {
    return null
  }
  return {
    correctedFeeCollected: reportItem?.feeCollected,
    adjustmentAmount: reportItem?.reconciliationAmount,
    note: reportItem?.reconciliationReason,
    appliedBy: reportItem?.reconciliationAppliedBy,
    appliedAt: reportItem?.reconciliationAppliedAt,
  }
}

export default function ReportPaymentDetails({ reportItem, embedded = false }) {
  const theme = useTheme()
  const [paymentDetails, setPaymentDetails] = useState(null)
  const [systemLogs, setSystemLogs] = useState(null)
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [systemLoading, setSystemLoading] = useState(false)
  const [paymentError, setPaymentError] = useState(null)
  const [systemError, setSystemError] = useState(null)
  const [tabValue, setTabValue] = useState(0)
  const reconciliationAdjustment = getReconciliationAdjustment(reportItem)

  const fetchPaymentDetailsFromRazorpay = async () => {
    if (!reportItem?.feePaidTxnId) {
      setPaymentError('Transaction ID is required to fetch payment details')
      return
    }

    setPaymentLoading(true)
    setPaymentError(null)

    try {
      const resp = await getSPDataWithParams(
        '/api/secure/cc/support/privileged/events/orders-and-payments?',
        { orderId: reportItem.feePaidTxnId },
      )
      if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
        const respData = getDataMapFromCommonResponse(resp)

        setPaymentDetails({
          orderEntity: respData?.orderEntity,
          paymentEntity: respData?.paymentEntity,
        })
      }
    } catch (err) {
      setPaymentError('Failed to fetch payment details')
      console.error('Error fetching payment details:', err)
    } finally {
      setPaymentLoading(false)
    }
  }

  const fetchSystemLogs = async () => {
    if (!reportItem?.feePaidTxnId) {
      setSystemError('Transaction ID is required to fetch system logs')
      return
    }

    setSystemLoading(true)
    setSystemError(null)

    try {
      const resp = await getSPDataWithParams(
        '/api/secure/cc/support/privileged/events/razorpay_payment_log/get?',
        { orderId: reportItem.feePaidTxnId },
      )
      if (resp?.status === 200) {
        setSystemLogs(resp?.data)
      }
    } catch (err) {
      setSystemError('Failed to fetch system logs')
      console.error('Error fetching system logs:', err)
    } finally {
      setSystemLoading(false)
    }
  }

  useEffect(() => {
    fetchPaymentDetailsFromRazorpay()
    fetchSystemLogs()
  }, [reportItem?.feePaidTxnId])

  const handleTabChange = (_, newValue) => {
    setTabValue(newValue)
  }

  const cardContent = (
    <Card
      sx={{
        maxWidth: embedded ? '100%' : 600,
        margin: embedded ? 0 : 'auto',
        mt: embedded ? 0 : 2,
      }}
    >
      <CardContent>
        <div style={{ marginBottom: theme.spacing(3) }}>
          <div
            style={{
              marginBottom: theme.spacing(2),
              padding: theme.spacing(2),
              border: '1px solid #e0e0e0',
              borderRadius: theme.shape.borderRadius,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Fee Collected
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {reportItem?.feeCollected
                ? getPriceStringWithCurrency(reportItem.feeCollected, 'INR')
                : 'No Data'}
            </Typography>
          </div>

          <div
            style={{
              marginBottom: theme.spacing(2),
              padding: theme.spacing(2),
              border: '1px solid #e0e0e0',
              borderRadius: theme.shape.borderRadius,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Fee for this session
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {reportItem?.feeCollectedFromPatient
                ? getPriceStringWithCurrency(reportItem.feeCollectedFromPatient, 'INR')
                : 'No Data'}
            </Typography>
          </div>

          <div
            style={{
              marginBottom: theme.spacing(2),
              padding: theme.spacing(2),
              border: '1px solid #e0e0e0',
              borderRadius: theme.shape.borderRadius,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Fee Paid Transaction ID
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {reportItem?.feePaidTxnId || 'No Data'}
            </Typography>
          </div>

          {/* Reconciliation Information */}
          {reportItem?.reconciliationApplied && (
            <div
              style={{
                marginBottom: theme.spacing(2),
                padding: theme.spacing(2),
                border: `1px solid ${theme.palette.warning.main}`,
                borderRadius: theme.shape.borderRadius,
                backgroundColor: theme.palette.warning.light,
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Reconciliation Applied
              </Typography>
              <div
                style={{
                  marginTop: theme.spacing(1),
                  display: 'flex',
                  flexDirection: 'column',
                  gap: theme.spacing(1),
                }}
              >
                <div>
                  <Typography variant="body2" color="text.secondary">
                    Corrected Fee Collected
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {reconciliationAdjustment?.correctedFeeCollected
                      ? getPriceStringWithCurrency(
                          reconciliationAdjustment.correctedFeeCollected,
                          reportItem?.currency || 'INR',
                        )
                      : 'No Data'}
                  </Typography>
                </div>
                <div>
                  <Typography variant="body2" color="text.secondary">
                    Adjustment Amount
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    color={
                      reconciliationAdjustment?.adjustmentAmount >= 0
                        ? 'error.main'
                        : 'success.main'
                    }
                  >
                    {reconciliationAdjustment?.adjustmentAmount !== undefined
                      ? `${reconciliationAdjustment.adjustmentAmount >= 0 ? '-' : '+'}${getPriceStringWithCurrency(
                          Math.abs(reconciliationAdjustment.adjustmentAmount),
                          reportItem?.currency || 'INR',
                        )}`
                      : 'No Data'}
                  </Typography>
                </div>
                {reconciliationAdjustment?.note && (
                  <div>
                    <Typography variant="body2" color="text.secondary">
                      Reason
                    </Typography>
                    <Typography variant="body2">{reconciliationAdjustment.note}</Typography>
                  </div>
                )}
                <div>
                  <Typography variant="body2" color="text.secondary">
                    Applied By
                  </Typography>
                  <Typography variant="body2">
                    {reconciliationAdjustment?.appliedBy || 'N/A'}
                  </Typography>
                </div>
                <div>
                  <Typography variant="body2" color="text.secondary">
                    Applied At
                  </Typography>
                  <Typography variant="body2">
                    {reconciliationAdjustment?.appliedAt
                      ? (() => {
                          const d = new Date(reconciliationAdjustment.appliedAt)
                          return Number.isNaN(d.getTime()) ? 'N/A' : d.toLocaleString()
                        })()
                      : 'N/A'}
                  </Typography>
                </div>
                {!!reconciliationAdjustment?.correctionHistory?.length && (
                  <div>
                    <Typography variant="body2" color="text.secondary">
                      Previous Corrections
                    </Typography>
                    <Typography variant="body2">
                      {reconciliationAdjustment.correctionHistory.length}
                    </Typography>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div style={{ marginBottom: theme.spacing(2) }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="payment details tabs">
            <Tab label="Razorpay Payment Details" />
            <Tab label="System Logs" />
          </Tabs>
        </div>

        <TabPanel value={tabValue} index={0}>
          {paymentLoading && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: theme.spacing(3),
              }}
            >
              <CircularProgress />
            </div>
          )}

          {paymentError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {paymentError}
            </Alert>
          )}

          {paymentDetails && (
            <div style={{ marginTop: theme.spacing(1) }}>
              <JsonView
                src={paymentDetails}
                theme="rjv-default"
                collapsed={false}
                displayDataTypes={false}
                displayObjectSize={false}
                enableClipboard={true}
                style={{
                  padding: '16px',
                  borderRadius: '8px',
                  fontSize: '14px',
                }}
              />
            </div>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {systemLoading && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: theme.spacing(3),
              }}
            >
              <CircularProgress />
            </div>
          )}

          {systemError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {systemError}
            </Alert>
          )}

          {systemLogs && (
            <div style={{ marginTop: theme.spacing(1) }}>
              <JsonView
                src={systemLogs}
                theme="rjv-default"
                collapsed={false}
                displayDataTypes={false}
                displayObjectSize={false}
                enableClipboard={true}
                style={{
                  padding: '16px',
                  borderRadius: '8px',
                  fontSize: '14px',
                }}
              />
            </div>
          )}
        </TabPanel>
      </CardContent>
    </Card>
  )

  if (embedded) {
    return cardContent
  }

  return (
    <>
      <DialogTitle>Payment Details</DialogTitle>
      <DialogContent>{cardContent}</DialogContent>
    </>
  )
}
